import { getAuth, createUserWithEmailAndPassword, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, onAuthStateChanged, sendEmailVerification, signInWithPopup, GoogleAuthProvider, deleteUser, updateEmail } from 'firebase/auth';
import { doc, getFirestore, runTransaction, setDoc, getDoc, getDocs, collection, query, where, addDoc, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import mixpanel from 'mixpanel-browser';
import { formatterLong, numberFormatter, percentage, statusDotColours } from '../styles/GlobalStyles';
import moment from 'moment';
import { arvPrompts, asIsPrompts, compSelectionPrompts, defaultPreferences, disqualifierPrompts, scoreWeightings } from '../defaults';
import { FullStory } from '@fullstory/browser';
import posthog from 'posthog-js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export const cloudFunctionV2 = async(url, data) => {
	return new Promise(async resolve => {
		await fetch(url, {
			method: 'POST',
			mode: 'cors',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				data: data
			})
		})
		.then(async(response) => {
			const content = await response.json();
			resolve(content);
		})
		.catch((error) => {
			if (error instanceof TypeError) {
				const res = {
					status: 406,
					error: "Load failed - network error"
				};
				resolve(res);
			}
		});
	});
};

export const cloudFunctionV2Body = async(url, data) => {
	return new Promise(async resolve => {
		await fetch(url, {
			method: 'POST',
			mode: 'cors',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify(data)
		})
		.then(async(response) => {
			const content = await response.json();
			resolve(content);
		})
		.catch((error) => {
			if (error instanceof TypeError) {
				const res = {
					status: 406,
					error: "Load failed - network error"
				};
				resolve(res);
			}
		});
	});
};

export const createUser = (email, password) => {
	const auth = getAuth();
	return new Promise(resolve => {
		createUserWithEmailAndPassword(auth, email, password)
		.then((userCredential) => {
			const data = {
				user: userCredential.user,
				status: 200,
				message: "Success",
				errorCode: ""
			}
			resolve(data);
		})
		.catch((error) => {
			const data = {
				user: "",
				status: 400,
				message: error.message,
				errorCode: error.code
			}
			resolve(data);
		});
	})
}

export const getUser = (email, password) => {
	const auth = getAuth();
	return new Promise(resolve => {
		signInWithEmailAndPassword(auth, email, password)
		.then((userCredential) => {
			const data = {
				code : "",
				message: "",
				status: 200,
				userId: userCredential.user.uid,
				user: userCredential.user
			};
			resolve(data);
		})
		.catch((error) => {
			const data = {
				code : error.code,
				message: error.message,
				status: 400,
				userId: "",
				user: ""
			};
			resolve(data);
		});
	});
}

export const signInWithGoogle = async() => {
	const auth = getAuth();
	const provider = new GoogleAuthProvider();
	return new Promise(resolve => {
		signInWithPopup(auth, provider)
		.then((result) => {
			const user = result.user;
			const data = {
				code : "",
				message: "",
				status: 200,
				userId: user.uid,
				user: user
			};
			resolve(data);
		}).catch((error) => {
			const data = {
				code : error.code,
				message: error.message,
				status: 400,
				userId: "",
				user: ""
			};
			resolve(data);
		});
	});
}

export const resetPassword = (email) => {
	const auth = getAuth();
	return new Promise(resolve => {
		sendPasswordResetEmail(auth, email)
		.then(() => {
			const success = {
				status: 200
			};
			resolve(success);
		})
		.catch((error) => {
			const errorCode = error.code;
			const errorMessage = error.message;
			const failure = {
				status: 400,
				code: errorCode,
				message: errorMessage
			}
			resolve(failure);
		});
	});
}

export const emailVerification = async() => {
	const auth = getAuth();

	return new Promise(resolve => {
		sendEmailVerification(auth.currentUser)
		.then(() => {
			resolve(true);
		})
		.catch((err) => {
			console.log("Email verification error = ", err);
			resolve(false);
		});
	});
}

export const updateUserEmail = async(email) => {
	const auth = getAuth();
	return new Promise(resolve => {
		updateEmail(auth.currentUser, email)
		.then(() => {
			const success = {
				status: 200
			}
			resolve(success);
		})
		.catch((error) => {
			const failure = {
				status: 400,
				error: error
			}
			resolve(failure);
		});
	});
};

export const logout = () => {
	const auth = getAuth();
	return new Promise(resolve => {
		signOut(auth)
		.then(() => {
			const data = {
				status: 200,
				message: ""
			};
			resetMixpanel();
			resolve(data);
		})
		.catch((error) => {
			const data = {
				status: 400,
				message: error
			};
			resolve(data);
		});
	});
}

export const userDeletion = (user) => {
	return new Promise(resolve => {
		deleteUser(user).then(() => {
			const data = {
				status: 200
			};
			resolve(data);
		}).catch((error) => {
			const data = {
				status: 400,
				message: error
			};
			resolve(data);
		});
	});
}

export const checkOnAuthStateChanged = async() => {
	const auth = getAuth();
	return new Promise(resolve => {
		onAuthStateChanged(auth, (user) => {
			if (user) {
				const providerData = user.providerData;
				let emailChangeable = true;
				for (let index = 0; index < providerData.length; index++) {
					const providerId = providerData[index].providerId;
					if ( providerId !== "password" ) {
						emailChangeable = false;
					}
				}
				const data = {
					status: 200,
					userId: user.uid,
					email: user.email,
					emailVerified: user.emailVerified,
					emailChangeable: emailChangeable,
					user: user
				};
	
				resolve(data);
			}
			else {
				const data = {
					status: 400,
					userId: "",
					email: "",
					emailVerified: "",
					emailChangeable: false
				};
				resolve(data);
			}
		});
	});
}

export const makeId = (length) => {
    var id = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for ( var i = 0; i < length; i++ ) {
       id += characters.charAt(Math.floor(Math.random() * charactersLength));
	}

	return id;
}

export const setData = (colRef, docRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		setDoc(doc(db, colRef, docRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			console.log("Set data error = ", e);
			console.log("Set data error data = ", data);
			resolve(res);
		});
	});
}

export const setSubData = (colRef, docRef, subColRef, subDocRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		setDoc(doc(db, colRef, docRef, subColRef, subDocRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			console.log("Error = ", e);
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			resolve(res);
		});
	});
};

export const setDataRandomId = (colRef, data) => {
	return new Promise(resolve => {
		const db = getFirestore();
		addDoc(collection(db, colRef), {
			data
		})
		.then(() => {
			const res = {
				status: 200,
				message: "Document created successfully"
			};
			resolve(res);
		})
		.catch((e) => {
			const res = {
				status: 400,
				message: `Error in creating document ${e}`
			};
			resolve(res);
		});
	});
}

export const setTransaction = async (colRef, docRef, root, val, subval) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				root === null ?
					data = val
				:
				subval === null ?
					data[root] = val
				:
					data[root][subval] = val
				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
}

export const setHiddenPropertyTransaction = async (colRef, docRef, subColRef, subDocRef, hiddenProperty) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				if ( data.hiddenProperties === undefined ) {
					data.hiddenProperties = [hiddenProperty];
				}
				else {
					const hiddenPropertiesArray = data.hiddenProperties;
					const index = hiddenPropertiesArray.findIndex(e => e.zpid === hiddenProperty.zpid)
					if ( index === -1 ) {
						hiddenPropertiesArray.push(hiddenProperty);
					}
					else {
						hiddenPropertiesArray.splice(index, 1);
					}
					data.hiddenProperties = hiddenPropertiesArray;
				}

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
}

export const setDefaultPropertyInfo = async (colRef, docRef, subColRef, subDocRef, selectedComps, arvData, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;
				data.arvData = arvData;
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setSelectedPropertyTransaction = async (colRef, docRef, subColRef, subDocRef, selectedComps) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setSelectedPropertyReportTransaction = async (colRef, docRef, subColRef, subDocRef, selectedComps) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};

				res = await setSubData(colRef, docRef, subColRef, subDocRef, {selectedComps: selectedComps})
			}
			else {
				let data = query.data().data;
				
				data.selectedComps = selectedComps;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setPropertyDescriptionsTransaction = async (colRef, docRef, subColRef, subDocRef, descriptions) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.propertyDescriptions = descriptions;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setPropertyARVData = async (colRef, docRef, subColRef, subDocRef, arvData, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvData = arvData;
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const setPropertyAsIsData = async (colRef, docRef, subColRef, subDocRef, asIsData) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.asIsData = asIsData;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const getDocumentNames = async (colRef) => {
    const db = getFirestore();
    const queryRef = collection(db, colRef);

    return new Promise(async (resolve) => {
        await getDocs(queryRef)
            .then((querySnapshot) => {
                const docNames = querySnapshot.docs.map((doc) => doc.id);
                const res = {
                    status: 200,
                    data: docNames,
                    message: "Documents retrieved successfully!"
                };
                resolve(res);
            })
            .catch((error) => {
                const res = {
                    status: 400,
                    data: [],
                    message: `Error retrieving document names: ${error}`,
                    code: error.code
                };
                resolve(res);
            });
    });
};

export const getDocument = async (colRef, docRef) => {
	const db = getFirestore();
	const query = doc(db, colRef, docRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
};

export const getSubDocument = (colRef, docRef, subColRef, subDocRef) => {
	const db = getFirestore();
	const query = doc(db, colRef, docRef, subColRef, subDocRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
}

export const getSubSubDocument = (colRef, docRef, subColRef, subDocRef, subSubColRef, subSubDocRef) => {
	const db = getFirestore();
	const query = doc(db, colRef, docRef, subColRef, subDocRef, subSubColRef, subSubDocRef);

	return new Promise(async resolve => {
		await getDoc(query)
		.then((doc) => {
			if ( doc.exists() ) {
				const res = {
					status: 200,
					data: doc.data(),
					message: "Document found!"
				}
				resolve(res);
			} 
			else {
				const res = {
					status: 400,
					data: [],
					message: "No such document!"
				}
				resolve(res);
			}
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				message: `Error getting document: ${error}`,
				code: error.code
			}
			resolve(res);
		});
    });
}

export const getAllDocuments = async(colRef) => {
	const db = getFirestore();
	const array = [];
	const ids = [];

	return new Promise(async resolve => {
		await getDocs(collection(db, colRef))
		.then((querySnapshot) => {
			querySnapshot.forEach((doc) => {
				const data = doc.data();
				array.push(data.data);
				ids.push(doc.id);
			});
			const res = {
				status: 200,
				data: array,
				ids: ids,
				message: "Document found!"
			}
			resolve(res);
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				ids: [],
				message: error,
				code: error.code
			}
			resolve(res);
		});
	});
}

export const getAllSubDocuments = async(colRef, docRef, subColRef) => {
	const db = getFirestore();
	const array = [];
	const ids = [];

	return new Promise(async resolve => {
		await getDocs(collection(db, colRef, docRef, subColRef))
		.then((querySnapshot) => {
			querySnapshot.forEach((doc) => {
				const data = doc.data();
				array.push(data.data);
				ids.push(doc.id);
			});
			const res = {
				status: 200,
				data: array,
				ids: ids,
				message: "Document found!"
			}
			resolve(res);
		})
		.catch((error) => {
			const res = {
				status: 400,
				data: [],
				ids: [],
				message: error,
				code: error.code
			}
			resolve(res);
		});
	});
}

export const getWhereDoc = async(col, field, operator, val) => {
	const db = getFirestore();
	const userRef = collection(db, col);
	const whereQuery = await query(userRef, where(field, operator, val));
	const queryDoc = await getDocs(whereQuery);
	
	const response = [];
	queryDoc.forEach((doc) => {
		const data = doc.data();
		response.push(data.data);
	});

	const res = {
		status: response.length === 0 ? 400 : 200,
		data: response
	};
	
	return res;
};

export const uploadImage = async(file, code, setLoading) => {
	const storage = getStorage();
	const storageRef = ref(storage, code);
	let blob;
	// If the caller passed a File/Blob (e.g. from an <input type="file">) use it directly.
	if (file instanceof Blob) {
		blob = file;
	} else {
		// Otherwise treat "file" as a URL/Data‑URI and fetch it.
		const response = await fetch(file);
		if (!response.ok) {
			throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
		}
		blob = await response.blob();
	}
	// Derive a sane MIME type – sometimes fetch() returns "text/html" for a failed request
	let mime = blob.type;
	if (!mime || mime.startsWith('text/')) {
		// Fallback to extension‑based guess
		const ext = code.split('.').pop().toLowerCase();
		if (ext === 'png')      mime = 'image/png';
		else if (ext === 'jpg' || ext === 'jpeg') mime = 'image/jpeg';
		else if (ext === 'gif') mime = 'image/gif';
		else                    mime = 'application/octet-stream';
	}
	const metadata = { contentType: mime };

	const uploadTask = uploadBytesResumable(storageRef, blob, metadata);

	return new Promise(resolve => {
		uploadTask.on('state_changed',
			(snapshot) => {
				// Observe state change events such as progress, pause, and resume
				// Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
				const progress = (snapshot.bytesTransferred / snapshot.totalBytes);
				setLoading(percentage.format(progress));
				console.log('Upload is ' + progress + '% done');
				switch (snapshot.state) {
				case 'paused':
					console.log('Upload is paused');
					break;
				case 'running':
					console.log('Upload is running');
					break;
				default:
					console.log("Upload default case");
				}
			},
			(error) => {
				console.log("Error = ", error);
				const res = {
					status: 400,
					message: error.message,
					code: error.code
				};
				resolve(res);
			},
			() => {
				getDownloadURL(uploadTask.snapshot.ref)
				.then(async(downloadURL) => {
					setLoading(null);
					const res = {
						status: 200,
						url: downloadURL
					};
					resolve(res);
				});
			}
		);
	})
}

export const resetMixpanel = () => {
	mixpanel.reset();
};

export const setMixpanelAlias = async(userId) => {
	mixpanel.alias(userId);
};

export const formatPrice = (value) => {
	const checkNaN = isNaN(value);
	const stringVal = `${value}`;
	if ( checkNaN === true ) {
		return value;
	}
	else if ( stringVal === "" ) {
		return "";
	}
	else if ( stringVal.includes(".") ) {
		const fullStopIndex = stringVal.indexOf('.');
		const firstString = stringVal.substring(0, fullStopIndex);
		const secondString = stringVal.substring(fullStopIndex, stringVal.length);
		const dollarNumber = formatterLong.format(Number(firstString)).replace("$", "").replace(".00", "");
		const removeExcessDPs = secondString.substring(0, 3);
		const finalNumber = `${dollarNumber}${removeExcessDPs}`;
		return finalNumber;
	}
	else {
		const dollarNumber = formatterLong.format(value).replace("$", "").replace(".00", "");
		return dollarNumber;
	}
};

export const recordEvent = (name, fields) => {
	mixpanel.track(name, fields);
	FullStory.event(name, fields);
	posthog.capture(name, fields);
}

export const generateEmptyEmailVerification = async(userId) => {
	const colRef = "email_verification";
	const data = [];

	return new Promise(async resolve => {
		const writeData = await setData(colRef, userId, data);
		resolve(writeData);
	});
};

export const propertyGatewayString = (prefix, params) => {
	const queryString = serializeParams(params);
	const pathWithQueryString = `${prefix}${queryString}`;
	return pathWithQueryString;
};

export const propertyGatewayStringReRun = (prefix, params) => {
	const queryString = serializeParams(params);
	const pathWithQueryString = `${prefix}${queryString}&forceRun=true`;
	return pathWithQueryString;
};

export const missingCompsString = (prefix, params) => {
	const queryString = `propertyId=${params.propertyId}&urls=${JSON.stringify(params.urls)}`;
	const pathWithQueryString = `${prefix}${queryString}`;
	return pathWithQueryString;
};

const serializeParams = (obj, prefix = '') => {
	const queryParts = [];
	for (const key in obj) {
		if (obj.hasOwnProperty(key)) {
			const value = obj[key];
			const prefixedKey = prefix ? `${prefix}.${key}` : key;

			if (typeof value === 'object' && !Array.isArray(value)) {
				// Recursively serialize nested objects
				queryParts.push(serializeParams(value, prefixedKey));
			} else {
				// Encode key and value, remove string quotations for non-nested values
				const encodedValue = encodeURIComponent(value);
				queryParts.push(`${prefixedKey}=${encodedValue}`);
			}
		}
	}
	return queryParts.join('&');
};

export const filterByARVType = (properties, types) => {
	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const flop = element.propertyDetails.compDetails.flop;
		const flip = element.propertyDetails.compDetails.flip;

		if ( types.includes("flop") && flop === true ) {
			newProps.push(element);
		}
		else if ( types.includes("flip") && flip === true && flop === false ) {
			newProps.push(element);
		}
		else if ( types.includes("non-flip") && flop === false && flip === false ) {
			newProps.push(element);
		}
	}
	return newProps;
};

export const filterByDistance = (properties, distance) => {
	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const distanceFrom = element.compDetails.distanceMiles;
		if ( distanceFrom <= distance ) {
			newProps.push(element);
		}
	}
	return newProps;
};

export const filterByCondition = (properties, conditions) => {
	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const propertyCondition = element.compDetails.propertyCondition;
		if ( conditions.includes("all") ) {
			newProps.push(element);
		}
		else if ( conditions.includes("remodelled") && propertyCondition.includes("Recently Remodeled") ) {
			newProps.push(element);
		}
		else if ( conditions.includes("well-maintained") && propertyCondition.includes("Well-Maintained") ) {
			newProps.push(element);
		}
		else if ( conditions.includes("moderate-rehab") && propertyCondition.includes("Moderate Rehab") ) {
			newProps.push(element);
		}
		else if ( conditions.includes("gut-rehab") && propertyCondition.includes("Gut Rehab") ) {
			newProps.push(element);
		}
		else if ( conditions.includes("unknown") && propertyCondition.includes("Unclear") ) {
			newProps.push(element);
		}
	};

	return newProps;
};

export const filterByRecency = (properties, months) => {
	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const listingStatus = element.listingDetails.listingStatus;
		const date = listingStatus === "for_sale" || listingStatus === "pending" ? element.listingDetails.saleListingDate : element.listingDetails.soldListingDate;
		const daysSinceListingOrSale = moment().diff(moment(date), "days");
		const monthsSinceListingOrSale = daysSinceListingOrSale / 30;
		if ( monthsSinceListingOrSale <= months ) {
			newProps.push(element);
		}
	};
	return newProps;
};

export const filterByType = (properties, types) => {
	if ( types.length === 3 ) {
		return properties;
	}
	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const listingStatus = element.listingDetails.listingStatus;
		if ( types.includes("sold") && listingStatus === "sold" ) {
			newProps.push(element);
		}
		else if ( types.includes("pending") && listingStatus === "pending" ) {
			newProps.push(element);
		}
		else if ( types.includes("for-sale") && listingStatus === "for_sale" ) {
			newProps.push(element);
		}
	}
	return newProps;
};

export const filterBySimilarity = (properties, similarities, subjectProperty) => {
	const bedsPlusMinus = similarities === null ? 100 : similarities.beds === null ? 100 : similarities.beds;
	const bathsPlusMinus = similarities === null ? 100 : similarities.baths === null ? 100 : similarities.baths;
	const sqFootagePlusMinus = similarities === null ? 10000 : similarities.sqft === null ? 10000 : similarities.sqft;
	const yearBuiltPlusMinus = similarities === null ? 10000 : similarities.yearBuilt === null ? 10000 : similarities.yearBuilt;

	const sjBeds = subjectProperty.propertyAttributes.bedrooms;
	const sjBaths = subjectProperty.propertyAttributes.bathrooms;
	const sjSqft = subjectProperty.propertyAttributes.livingSquareFootage;
	const sjYearBuilt = subjectProperty.propertyAttributes.yearBuilt;

	const newProps = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const compBeds = element.propertyAttributes.bedrooms;
		const compBaths = element.propertyAttributes.bathrooms;
		const compSqft = element.propertyAttributes.livingSquareFootage;
		const compYearBuilt = element.propertyAttributes.yearBuilt;

		const bedsDiff = Math.abs(sjBeds - compBeds);
		const bathsDiff = Math.abs(sjBaths - compBaths);
		const sqftDiff = Math.abs(sjSqft - compSqft);
		const yearBuiltDiff = Math.abs(sjYearBuilt - compYearBuilt);

		if ( bedsDiff <= bedsPlusMinus && bathsDiff <= bathsPlusMinus && sqftDiff <= sqFootagePlusMinus && yearBuiltDiff <= yearBuiltPlusMinus ) {
			newProps.push(element);
		}
	}

	return newProps;
};

export const sortComps = (properties, sortType) => {
	if ( sortType === "group" ) {
		properties.sort((a, b) => a.propertyDetails.compClusterScore.group - b.propertyDetails.compClusterScore.group);
	}
	else if ( sortType === "group-desc" ) {
		properties.sort((a, b) => b.propertyDetails.compClusterScore.group - a.propertyDetails.compClusterScore.group);
	}
	else if ( sortType === "address" ) {
		properties.sort((a, b) => a.propertyDetails.addressDetails.streetAddress.localeCompare(b.propertyDetails.addressDetails.streetAddress));
	}
	else if ( sortType === "address-desc" ) {
		properties.sort((a, b) => b.propertyDetails.addressDetails.streetAddress.localeCompare(a.propertyDetails.addressDetails.streetAddress));
	}
	else if ( sortType === "distance" ) {
		properties.sort((a, b) => a.propertyDetails.compDetails.distanceMiles - b.propertyDetails.compDetails.distanceMiles);
	}
	else if ( sortType === "distance-desc" ) {
		properties.sort((a, b) => b.propertyDetails.compDetails.distanceMiles - a.propertyDetails.compDetails.distanceMiles);
	}
	else if ( sortType === "comp-type" ) {

	}
	else if ( sortType === "comp-type-desc" ) {
		
	}
	else if (sortType === "flip-type") {
		properties.sort((a, b) => {
			const priorityA = a.propertyDetails.compDetails.flip && !a.propertyDetails.compDetails.flop
				? 0
				: a.propertyDetails.compDetails.flop
				? 1
				: 2;

			const priorityB = b.propertyDetails.compDetails.flip && !b.propertyDetails.compDetails.flop
				? 0
				: b.propertyDetails.compDetails.flop
				? 1
				: 2;

			return priorityA - priorityB;
		});
	}
	else if (sortType === "flip-type-desc") {
		properties.sort((a, b) => {
			const priorityA = a.propertyDetails.compDetails.flip && !a.propertyDetails.compDetails.flop
				? 0
				: a.propertyDetails.compDetails.flop
				? 1
				: 2;

			const priorityB = b.propertyDetails.compDetails.flip && !b.propertyDetails.compDetails.flop
				? 0
				: b.propertyDetails.compDetails.flop
				? 1
				: 2;

			return priorityB - priorityA;
		});
	}
	else if ( sortType === "status" ) {
		properties.sort((a, b) => a.propertyDetails.listingDetails.listingStatus.localeCompare(b.propertyDetails.listingDetails.listingStatus));
	}
	else if ( sortType === "status-desc" ) {
		properties.sort((a, b) => b.propertyDetails.listingDetails.listingStatus.localeCompare(a.propertyDetails.listingDetails.listingStatus));
	}
	else if ( sortType === "price" ) {
		properties.sort((a, b) => (
			(a.propertyDetails.listingDetails.listingStatus === "for_sale" ? a.propertyDetails.listingDetails.listingPrice : a.propertyDetails.listingDetails.soldListingPrice) - 
			(b.propertyDetails.listingDetails.listingStatus === "for_sale" ? b.propertyDetails.listingDetails.listingPrice : b.propertyDetails.listingDetails.soldListingPrice)
		));
	}
	else if ( sortType === "price-desc" ) {
		properties.sort((a, b) => (
			(b.propertyDetails.listingDetails.listingStatus === "for_sale" ? b.propertyDetails.listingDetails.listingPrice : b.propertyDetails.listingDetails.soldListingPrice) - 
			(a.propertyDetails.listingDetails.listingStatus === "for_sale" ? a.propertyDetails.listingDetails.listingPrice : a.propertyDetails.listingDetails.soldListingPrice)
		));
	}
	else if ( sortType === "date" ) {
		properties.sort((a, b) => (
			new Date(b.propertyDetails.listingDetails.listingStatus === "for_sale" ? 
				b.propertyDetails.listingDetails.saleListingDate : 
				b.propertyDetails.listingDetails.soldListingDate
			) - 
			new Date(a.propertyDetails.listingDetails.listingStatus === "for_sale" ? 
				a.propertyDetails.listingDetails.saleListingDate : 
				a.propertyDetails.listingDetails.soldListingDate
			)
		));
	}
	else if ( sortType === "date-desc" ) {
		properties.sort((a, b) => (
			new Date(a.propertyDetails.listingDetails.listingStatus === "for_sale" ? 
				a.propertyDetails.listingDetails.saleListingDate : 
				a.propertyDetails.listingDetails.soldListingDate
			) - 
			new Date(b.propertyDetails.listingDetails.listingStatus === "for_sale" ? 
				b.propertyDetails.listingDetails.saleListingDate : 
				b.propertyDetails.listingDetails.soldListingDate
			)
		));
	}
	else if (sortType === "days") {
		properties.sort((a, b) => {
			const daysA = a.propertyDetails.listingDetails.listingStatus === "for_sale"
				? moment().diff(moment(a.propertyDetails.listingDetails.saleListingDate), 'days')
				: a.propertyDetails.listingDetails.daysUntilSold;

			const daysB = b.propertyDetails.listingDetails.listingStatus === "for_sale"
				? moment().diff(moment(b.propertyDetails.listingDetails.saleListingDate), 'days')
				: b.propertyDetails.listingDetails.daysUntilSold;

			return daysB - daysA; // Sort descending (most recent first)
		});
	}
	else if (sortType === "days-desc") {
		properties.sort((a, b) => {
			const daysA = a.propertyDetails.listingDetails.listingStatus === "for_sale"
				? moment().diff(moment(a.propertyDetails.listingDetails.saleListingDate), 'days')
				: a.propertyDetails.listingDetails.daysUntilSold;

			const daysB = b.propertyDetails.listingDetails.listingStatus === "for_sale"
				? moment().diff(moment(b.propertyDetails.listingDetails.saleListingDate), 'days')
				: b.propertyDetails.listingDetails.daysUntilSold;

			return daysA - daysB; // Sort ascending (oldest first)
		});
	}
	else if ( sortType === "beds" ) {
		properties.sort((a, b) => a.propertyDetails.propertyAttributes.bedrooms - b.propertyDetails.propertyAttributes.bedrooms);
	}
	else if ( sortType === "beds-desc" ) {
		properties.sort((a, b) => b.propertyDetails.propertyAttributes.bedrooms - a.propertyDetails.propertyAttributes.bedrooms);
	}
	else if ( sortType === "baths" ) {
		properties.sort((a, b) => a.propertyDetails.propertyAttributes.bathrooms - b.propertyDetails.propertyAttributes.bathrooms);
	}
	else if ( sortType === "baths-desc" ) {
		properties.sort((a, b) => b.propertyDetails.propertyAttributes.bathrooms - a.propertyDetails.propertyAttributes.bathrooms);
	}
	else if ( sortType === "built" ) {
		properties.sort((a, b) => a.propertyDetails.propertyAttributes.yearBuilt - b.propertyDetails.propertyAttributes.yearBuilt);
	}
	else if ( sortType === "built-desc" ) {
		properties.sort((a, b) => b.propertyDetails.propertyAttributes.yearBuilt - a.propertyDetails.propertyAttributes.yearBuilt);
	}
	else if ( sortType === "sqft" ) {
		properties.sort((a, b) => a.propertyDetails.propertyAttributes.livingSquareFootage - b.propertyDetails.propertyAttributes.livingSquareFootage);
	}
	else if ( sortType === "sqft-desc" ) {
		properties.sort((a, b) => b.propertyDetails.propertyAttributes.livingSquareFootage - a.propertyDetails.propertyAttributes.livingSquareFootage);
	}
	else if ( sortType === "lot" ) {
		properties.sort((a, b) => a.propertyDetails.propertyAttributes.lotSquareFootage - b.propertyDetails.propertyAttributes.lotSquareFootage);
	}
	else if ( sortType === "lot-desc" ) {
		properties.sort((a, b) => b.propertyDetails.propertyAttributes.lotSquareFootage - a.propertyDetails.propertyAttributes.lotSquareFootage);
	}
	else if ( sortType === "score" ) {
		properties.sort((a, b) => b.propertyDetails.compDetails.overallScore - a.propertyDetails.compDetails.overallScore);
	}
	else {
		properties.sort((a, b) => a.propertyDetails.compDetails.distanceMiles - b.propertyDetails.compDetails.distanceMiles);
	}

	return properties;
};

export const getPercentile = (arr, percentile) => {
    if (!Array.isArray(arr) || arr.length === 0) {
        throw new Error("Input must be a non-empty array.");
    }

    // Sort the array in ascending order
    const sortedArr = [...arr].sort((a, b) => a - b);

    const index = (percentile / 100) * (sortedArr.length - 1);
    const lower = Math.floor(index);
    const upper = Math.ceil(index);
    const weight = index % 1;

    // Interpolate if necessary
    return lower === upper
        ? sortedArr[lower]
        : sortedArr[lower] * (1 - weight) + sortedArr[upper] * weight;
};

export const propertyTypeFormat = (propertyType) => {
	return propertyType === "Single%20Family" ? "Single Family" : propertyType === "CONDO" ? "Condo" : propertyType === "SFR" ? "Single Family" : propertyType === "MFR" ? "Multi Family" : propertyType === "TWNH" ? "Townhouse" : propertyType;
};

export const propertyTypeFormatAbbreviated = (propertyType) => {
	return propertyType === "Single%20Family" ? "Single Family" : propertyType === "CONDO" ? "Con" : propertyType === "SFR" ? "SFH" : propertyType === "MFR" ? "MFH" : propertyType === "TWNH" ? "Tow" : propertyType;
};

export const formatDaysOnMarket = (date) => {
	if ( date === null ) {
		return "N/A";
	}
	const daysSinceToday = moment().diff(moment(date), "days");
	if ( daysSinceToday >= 730 ) {
		// Over 2 years ago
		const formattedDate = moment(date).format("MMM DD, YYYY");
		return formattedDate;
	}
	else if ( daysSinceToday > 99 ) {
		// 100 days or over
		const monthsDifference = moment().diff(moment(date), "months");
		const formattedDate = `${monthsDifference} month${monthsDifference === 1 ? "" : "s"} ago`;
		return formattedDate;
	}
	else {
		// Under 100 days
		const formattedDate = `${daysSinceToday} day${daysSinceToday === 1 ? "" : "s"} ago`;
		return formattedDate;
	}
};

export const generateSelectedCompsArray = async(fullSelectedComps, selectedComps, value) => {
	const selectedCompSubmission = [];
	for (let index = 0; index < fullSelectedComps.length; index++) {
		const element = fullSelectedComps[index];
		const zpid = element.uniqueIds.zillowId;
		const selectedCompIndex = selectedComps.findIndex(e => e.zpid === zpid);
		if ( selectedCompIndex === -1 ) {
			continue;
		}
		const selectedCompType = selectedComps[selectedCompIndex].type;
		if ( selectedCompType === value ) {
			const newObject = await generatePropertyObject(element);
			selectedCompSubmission.push(newObject);
		}
	};
	return selectedCompSubmission;
};

export const getAllARVComps = async(allComps) => {
	const allCompsArray = [];
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		const overallScore = element.compDetails.overallScore;
		if ( overallScore < 0.5 ) {
			continue;
		}
		const propertyCondition = element.compDetails.propertyCondition;
		if (
			propertyCondition === "Recently Remodeled" ||
			propertyCondition === "Well-Maintained – Traditional" ||
			propertyCondition === "Well-Maintained – Contemporary"
		) {
			allCompsArray.push(element);
		}
	};
	return allCompsArray;
};

export const checkIfCompsTight = async(bestComps, subjectProperty) => {
	bestComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
	const minifiedComps = await minifyComps(bestComps);
	const minifiedSubjectProperty = await generateSubjectPropertyObject(subjectProperty);

	const promptPayload = compSelectionPrompts.initial({
		comps: minifiedComps,
		subjectProperty: minifiedSubjectProperty,
		message: ""
	});

	const prompt = promptPayload.prompt;
	const systemInstructions = promptPayload.system;
	const gptURL = process.env.REACT_APP_QUERY_GPT;

	return new Promise(async resolve => {
		const gptParams = {
			service: "openai",
			model: "o4-mini",
			prompt: prompt,
			systemInstructions: systemInstructions
		};
		const queryGPTData = await cloudFunctionV2(gptURL, gptParams);
		if ( queryGPTData.status === 200 ) {
			const res = {
				status: 200,
				data: queryGPTData.data
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryGPTData.error
			};
			resolve(res);
		}
	});
};

export const calculateTokenCost = ({prompt_tokens, cached_tokens, completion_tokens}) => {
	const PRICE = {input: 10, cached: 2.5, output: 40};     // O3 Pricing per 1M tokens
	const uncached = prompt_tokens - cached_tokens;

	const inputCost   = (uncached         * PRICE.input)  / 1e6;
	const cachedCost  = (cached_tokens    * PRICE.cached) / 1e6;
	const outputCost  = (completion_tokens* PRICE.output) / 1e6;

	return {
		inputCost,
		cachedCost,
		outputCost,
		total: inputCost + cachedCost + outputCost
	};
}

/**
 * Unified helper for all OpenAI requests so that every call is handled
 * consistently and errors bubble via throw/try‑catch (no more nested Promises).
 */
export const queryGPT = async (gptURL, messages) => {
	const params = {
		service: "openai",
		messages,
		model: "o3",
		responseFormat: {
			type: "json_object"
		}
	};
	const res = await cloudFunctionV2(gptURL, params);
	if (res.status !== 200) {
		throw new Error(res.error || "GPT query failed");
	}
	const raw = res.data;
	// const usage = {
	// 	prompt_tokens: res.usage.prompt_tokens,
	// 	cached_tokens: res.usage.prompt_tokens_details.cached_tokens,
	// 	completion_tokens: res.usage.completion_tokens
	// };
	// const tokenCost = await calculateTokenCost(usage);
	return typeof raw === "string"
		? JSON.parse(raw.replace(/```json|```/g, "").trim())
		: raw;
};

export const checkConditions = async(comps, setSelectedConditions, allComps, setFilteredComps) => {
	let remodelled = false;
	let wellMaintained = false;
	let moderateRehab = false;
	let gutRehab = false;
	let unknown = false;

	for (let index = 0; index < comps.length; index++) {
		const element = comps[index];
		const propertyCondition = element.compDetails.propertyCondition;
		if ( propertyCondition.includes("Recently Remodeled") ) {
			remodelled = true;
		}
		else if ( propertyCondition.includes("Well-Maintained") ) {
			wellMaintained = true;
		}
		else if ( propertyCondition.includes("Moderate Rehab") ) {
			moderateRehab = true;
		}
		else if ( propertyCondition.includes("Gut Rehab") ) {
			gutRehab = true;
		}
		else if ( propertyCondition.includes("Unclear") ) {
			unknown = true;
		}
	}

	const finalArray = [];
	if ( 
		remodelled === true &&
		wellMaintained === true &&
		moderateRehab === true &&
		gutRehab === true &&
		unknown === true
	) {
		finalArray.push("all");
		return;
	}
	
	if ( remodelled === true ) {
		finalArray.push("remodelled");
	}
	if ( wellMaintained === true ) {
		finalArray.push("well-maintained");
	}
	if ( moderateRehab === true ) {
		finalArray.push("moderate-rehab");
	}
	if ( gutRehab === true ) {
		finalArray.push("gut-rehab");
	}
	if ( unknown === true ) {
		finalArray.push("unknown");
	}
	setSelectedConditions(finalArray);

	const newFilteredComps = [];
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		const propertyCondition = element.compDetails.propertyCondition;
		if ( finalArray.includes("all") ) {
			newFilteredComps.push(element);
		}
		else if ( finalArray.includes("remodelled") && propertyCondition.includes("Recently Remodeled") ) {
			newFilteredComps.push(element);
		}
		else if ( finalArray.includes("well-maintained") && propertyCondition.includes("Well-Maintained") ) {
			newFilteredComps.push(element);
		}
		else if ( finalArray.includes("moderate-rehab") && propertyCondition.includes("Moderate Rehab") ) {
			newFilteredComps.push(element);
		}
		else if ( finalArray.includes("gut-rehab") && propertyCondition.includes("Gut Rehab") ) {
			newFilteredComps.push(element);
		}
		else if ( finalArray.includes("unknown") && propertyCondition.includes("Unclear") ) {
			newFilteredComps.push(element);
		}
	};

	newFilteredComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
	setFilteredComps(newFilteredComps);
};

export const startCompSelectionAgent = async(
	allComps, 
	subjectProperty, 
	companyId,
	propertyId,
	propertyNotes,
	adu,
	pool,
	garage,
	settings,
	anomalyAnswered,
	anomalyData
) => {
	const testMode = window.location.hostname === "localhost";
	const url = process.env.REACT_APP_AGENTIC_ARV;

	if ( propertyId === "4167249" ) {
		subjectProperty.propertyAttributes.pool = false;
	}
	subjectProperty.listingDetails.aduFlag = adu;
	subjectProperty.propertyAttributes.pool = pool;
	subjectProperty.propertyAttributes.garage = garage;

	const params = {
		subjectProperty: subjectProperty,
		allComps: allComps,
		model: "o3",
		companyId: companyId,
		propertyId: propertyId,
		temperature: 1,
		minLocationScore: 0.8,
		propertyNotes: propertyNotes,
		rewriteProvider: "openai",
		rewriteModel: "gpt-4o",
		testMode: testMode,
		sms: false,
		anomalyAnswered: anomalyAnswered,
		anomalyRes: anomalyData,
		filterEssential: {
			construction_materials: settings ? settings.constructionMaterials : true,
			pools: settings ? settings.pool : true,
			adu: settings ? settings.adu : true,
			stories: settings ? settings.stories : false,
			bed_bath: false
		}
	};
	return new Promise(async resolve => {
		const queryData = await cloudFunctionV2(url, params);
		if ( queryData.status === 200 ) {
			console.log("DONE AGENTIC");
			console.log(queryData)
			const res = {
				status: 200,
				data: queryData
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryData.error
			};
			resolve(res);
		}
	});
};

export const getBestInitialARVComps = async(allComps) => {
	allComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);

	const initialMinScore = 0.7;
	const atLeastXScore = [];
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		const overallScore = element.compDetails.overallScore;
		if ( overallScore >= initialMinScore ) {
			atLeastXScore.push(element);
		}
	};

	const initialSoldCount = atLeastXScore.filter(e => e.listingDetails.listingStatus === "sold").length;
	if ( initialSoldCount >= 3 ) {
		// We have at least 3 good sold comps
		const bestSoldComps = atLeastXScore.filter(e => e.listingDetails.listingStatus === "sold").slice(0, 3);
		for (let index = 0; index < atLeastXScore.length; index++) {
			const element = atLeastXScore[index];
			if ( bestSoldComps.length >= 5 ) {
				break;
			}

			const zpid = element.uniqueIds.zillowId;
			const zpidIndex = bestSoldComps.findIndex(e => e.uniqueIds.zillowId === zpid);
			if ( zpidIndex !== -1 ) {
				continue;
			}
			bestSoldComps.push(element);
		}

		bestSoldComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
		return bestSoldComps;
	}
	else {

		// We do NOT have at least 3 good sold comps
		const minLocationScore = 0.8;
		const bestSoldLocationComps = [];
		for (let index = 0; index < allComps.length; index++) {
			const element = allComps[index];
			const status = element.listingDetails.listingStatus;
			if ( status !== "sold" ) {
				continue;
			}
			const zpid = element.uniqueIds.zillowId;
			const zpidIndex = atLeastXScore.findIndex(e => e.uniqueIds.zillowId === zpid);
			if ( zpidIndex !== -1 ) {
				continue;
			}
			const locationScore = element.compDetails.locationScore;
			if ( locationScore >= minLocationScore ) {
				bestSoldLocationComps.push(element);
			}
		}
		bestSoldLocationComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
		const neededSoldComps = 3 - initialSoldCount;
		const bestSoldComps = bestSoldLocationComps.slice(0, neededSoldComps);
		for (let index = 0; index < atLeastXScore.length; index++) {
			const element = atLeastXScore[index];
			if ( bestSoldComps.length >= 5 ) {
				break;
			}

			const zpid = element.uniqueIds.zillowId;
			const zpidIndex = bestSoldComps.findIndex(e => e.uniqueIds.zillowId === zpid);
			if ( zpidIndex !== -1 ) {
				continue;
			}
			bestSoldComps.push(element);
		}
		
		bestSoldComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
		return bestSoldComps;
	}
};

export const getBestARVComps = async(allComps) => {
	const newFilteredComps = [];
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		const propertyCondition = element.compDetails.propertyCondition;
		if ( 
			propertyCondition === "Recently Remodeled" ||
			propertyCondition === "Well-Maintained – Traditional" ||
			propertyCondition === "Well-Maintained – Contemporary"
		) {
			newFilteredComps.push(element);
		}
	};

	const newBestComps = await getBestInitialARVComps(newFilteredComps);
	return newBestComps;
};

export const minifyComps = async(allComps) => {
	const newArray = [];
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		const newObject = await generatePropertyObject(element);
		newArray.push(newObject);
	};
	return newArray;
};

export const maxifyComps = async(zpids, allComps) => {
	const newArray = [];
	for (let index = 0; index < zpids.length; index++) {
		const zpid = zpids[index];
		const zpidIndex = allComps.findIndex(e => e.uniqueIds.zillowId === zpid);
		if ( zpidIndex !== -1 ) {
			const element = allComps[zpidIndex];
			newArray.push(element);
		}
	};
	return newArray;
};

export const getNextSoldComps = async(allComps, bestComps) => {
	const nextBestSoldComps = [];
	const compLimit = 3;

	allComps.sort((a, b) => b.compDetails.overallScore - a.compDetails.overallScore);
	for (let index = 0; index < allComps.length; index++) {
		const element = allComps[index];
		if ( nextBestSoldComps.length >= compLimit ) {
			break;
		}

		const zpid = element.uniqueIds.zillowId;
		const zpidIndex = bestComps.findIndex(e => e.uniqueIds.zillowId === zpid);
		if ( zpidIndex !== -1 ) {
			continue;
		}

		const listingStatus = element.listingDetails.listingStatus;
		if ( listingStatus !== "sold" ) {
			continue;
		}

		nextBestSoldComps.push(element);
	};

	return nextBestSoldComps;
};

export const generateSubjectPropertyObject = async(element) => {
	const constructionType = element.propertyAttributes.constructionTypeBySource === undefined || element.propertyAttributes.constructionTypeBySource === null || element.propertyAttributes.constructionTypeBySource.reapi === null ? element.propertyAttributes.constructionType : element.propertyAttributes.constructionTypeBySource.reapi;
	const subjectPropertySubmission = {
		propertyInfo: {
			propertyType: propertyTypeFormat(element.propertyAttributes.propertyType),
			bedrooms: element.propertyAttributes.bedrooms,
			bathrooms: element.propertyAttributes.bathrooms,
			sqFootage: element.propertyAttributes.livingSquareFootage,
			yearBuilt: element.propertyAttributes.yearBuilt,
			lotSize: element.propertyAttributes.lotSquareFootage,
			garage: element.propertyAttributes.garage,
			pool: element.propertyAttributes.pool,
			subdivision: element.propertyAttributes.subdivision,
			waterfront: element.propertyAttributes.waterFront,
			constructionType: constructionType === "" ? "Unknown" : constructionType,
			censusBlock: element.addressDetails.censusBlock,
			censusTract: element.addressDetails.geoId,
			stories: element.propertyAttributes.stories,
			adu: element.listingDetails.aduFlag ? true : false
		},
		status: element.listingDetails.listingStatus === "for_sale" ? "For sale" : element.listingDetails.listingStatus === "pending" ? "Pending" : "Sold",
		address: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
		latitude: element.addressDetails.latitude,
		longitude: element.addressDetails.longitude
	};

	if ( element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ) {
		subjectPropertySubmission.listingPrice = element.listingDetails.listingPrice;
		subjectPropertySubmission.originalListingPrice = element.listingDetails.saleListingPrice;
		subjectPropertySubmission.totalPriceChanges = element.listingDetails.totalPriceChanges;
		subjectPropertySubmission.listingDate = element.listingDetails.saleListingDate;
	}
	else if ( element.listingDetails.soldListingPrice !== null ) {
		subjectPropertySubmission.lastSoldDate = element.listingDetails.soldListingDate;
		subjectPropertySubmission.lastSoldPrice = element.listingDetails.soldListingPrice;
	}

	if ( element.propertyAttributes.floodZoneType !== undefined && element.propertyAttributes.floodZoneType !== null ) {
		subjectPropertySubmission.floodZoneType = element.propertyAttributes.floodZoneType;
		if ( 
			element.propertyAttributes.floodZoneType.includes("A") ||
			element.propertyAttributes.floodZoneType.includes("V")
		) {
			subjectPropertySubmission.floodZone = true;
		}
	}
	else {
		subjectPropertySubmission.floodZoneType = "Unknown";
	}

	return subjectPropertySubmission;
};

export const generatePropertyObject = async(element) => {
	const propertyType = element.propertyAttributes.propertyType;
	const flipStatus = element.compDetails.flop === true ? "Flop" : element.compDetails.flip === true ? "Flip" : "Non-flip";
	const price = getPriceField(element)
	const recency = element.listingDetails.listingStatus === "for_sale" || element.listingDetails.listingStatus === "pending" ? `${element.novationDOM} days` : formatDaysOnMarket(element.listingDetails.soldListingDate);
	const constructionType = element.propertyAttributes.constructionTypeBySource === undefined || element.propertyAttributes.constructionTypeBySource === null || element.propertyAttributes.constructionTypeBySource.reapi === null ? element.propertyAttributes.constructionType : element.propertyAttributes.constructionTypeBySource.reapi;
	const compInfo = {
		propertyInfo: {
			propertyType: propertyTypeFormat(propertyType),
			bedrooms: element.propertyAttributes.bedrooms,
			bathrooms: element.propertyAttributes.bathrooms,
			sqFootage: element.propertyAttributes.livingSquareFootage,
			yearBuilt: element.propertyAttributes.yearBuilt,
			lotSize: element.propertyAttributes.lotSquareFootage,
			garage: element.propertyAttributes.garage,
			pool: element.propertyAttributes.pool,
			listingDescription: element.listingDetails.listingDescriptionValidFlag === false ? "" : element.listingDetails.listingDescription,
			subdivision: element.propertyAttributes.subdivision,
			waterfront: element.propertyAttributes.waterFront,
			constructionType: constructionType === "" ? "Unknown" : constructionType,
			censusBlock: element.addressDetails.censusBlock,
			censusTract: element.addressDetails.geoId,
			stories: element.propertyAttributes.stories,
			adu: element.listingDetails.aduFlag
		},
		distanceMiles: element.compDetails.distanceMiles,
		highwayObstacle: element.compDetails.highway,
		waterObstacle: element.compDetails.water,
		railroadObstacle: element.compDetails.railroad,
		condition: element.compDetails.propertyCondition,
		flipStatus: flipStatus,
		status: element.listingDetails.listingStatus === "for_sale" ? "For sale" : element.listingDetails.listingStatus === "pending" ? "Pending" : "Sold",
		daysOnMarket: recency,
		price: price,
		pricePerSqFt: element.propertyAttributes.livingSquareFootage === null || element.propertyAttributes.livingSquareFootage === 0 ? 0 : price / element.propertyAttributes.livingSquareFootage,
		scores: {
			overall: element.compDetails.overallScore,
			location: element.compDetails.locationScore,
			recency: element.compDetails.recencyScore,
			similarity: element.compDetails.similarityScore
		},
		address: `${element.addressDetails.streetAddress}, ${element.addressDetails.city}, ${element.addressDetails.state} ${element.addressDetails.postalCode}`,
		latitude: element.addressDetails.latitude,
		longitude: element.addressDetails.longitude,
		zpid: element.uniqueIds.zillowId
	};

	if ( element.propertyAttributes.floodZoneType !== undefined && element.propertyAttributes.floodZoneType !== null ) {
		compInfo.floodZoneType = element.propertyAttributes.floodZoneType;
		if ( 
			element.propertyAttributes.floodZoneType.includes("A") ||
			element.propertyAttributes.floodZoneType.includes("V")
		) {
			compInfo.floodZone = true;
		}
	}
	else {
		compInfo.floodZoneType = "Unknown";
	}

	return compInfo;
};

export const propertyConditionBG = (condition, darkMode) => {
	if ( condition === "Recently Remodeled" ) {
		return "bg-colour-green-02";
	}
	else if ( condition === "Well-Maintained – Contemporary" ) {
		return "bg-colour-aqua-02";
	}
	else if ( condition === "Well-Maintained – Traditional" ) {
		return "bg-colour-yellow-02";
	}
	else if ( condition === "Moderate Rehab" ) {
		return "bg-colour-orange-02";
	}
	else if ( condition === "Gut Rehab" ) {
		return "bg-colour-red-02";
	}
	else {
		return "";	
	}
};

export const getPriceField = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return property.listingDetails.listingPrice;
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return property.listingDetails.listingPrice;
	}
	else {
		return property.listingDetails.soldListingPrice;
	}
};

export const propertyStatusBG = (property, darkMode) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return darkMode ? "bg-colour-for-sale-dark" : "bg-colour-for-sale";
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return darkMode ? "bg-colour-pending-dark" : "bg-colour-pending";
	}
	else {
		return darkMode ? "bg-colour-sold-dark" : "bg-colour-sold";
	}
};
export const propertyStatusText = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return "For sale";
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return "Pending";
	}
	else {
		return "Sold";
	}
};

export const getStatusDotColour = (property) => {
	if ( property.listingDetails.listingStatus === "for_sale" ) {
		return statusDotColours.forSale;
	}
	else if ( property.listingDetails.listingStatus === "pending" ) {
		return statusDotColours.pending;
	}
	else {
		return statusDotColours.sold;
	}
};

export const squareFeetToAcres = (sqft) => {
    if (typeof sqft !== "number" || sqft < 0) return "N/A";
    
    const acres = sqft / 43560;
    
    // If less than 0.25 acres, return square footage
    if (acres < 0.25) {
        return `${numberFormatter.format(sqft)} sqft lot`;
    } 
    // If between 0.25 and 1 acre, round to 1 decimal place and ensure a trailing zero
    else if (acres < 1) {
        return `${(Math.round(acres * 10) / 10).toFixed(1)} acres`;
    } 
    // If exactly 1 acre, return without decimals or "s"
    else if (acres === 1) {
        return `1 acre`;
    }

    // If greater than 1 acre, round to 1 decimal place
    return `${Math.round(acres * 10) / 10} acres`;
};

export const getWordsFromCharacters = (characters, timestampsStart) => {
	if (!Array.isArray(characters) || !Array.isArray(timestampsStart)) {
		throw new Error("Invalid input data: characters or timestampsStart is not an array.");
	}

	const words = [];
	let currentWord = "";
	let currentWordStartIndex = null;

	for (let i = 0; i < characters.length; i++) {
		const char = characters[i];
		// Check if this character is whitespace (space, tab, newline, etc.)
		const isWhitespace = /\s/.test(char);

		if (!isWhitespace) {
			// Start of a new word?
			if (currentWord.length === 0) {
				currentWordStartIndex = i;
			}
				currentWord += char;
			} 
		else {
			// If we just ended a word, push it to the results
			if (currentWord.length > 0) {
				words.push({
					word: currentWord,
					startSec: timestampsStart[currentWordStartIndex]
				});
				// Reset for the next word
				currentWord = "";
				currentWordStartIndex = null;
			}
		}
	}

	// If the last character wasn't whitespace, we may have one more word to push
	if (currentWord.length > 0 && currentWordStartIndex !== null) {
		words.push({
			word: currentWord,
			startSec: timestampsStart[currentWordStartIndex]
		});
	}

  	return words;
}

export const saveDefaultPreferences = async(userId) => {
	return new Promise(async resolve => {
		const colRef = "settings";
		const writeData = await setData(colRef, userId, defaultPreferences);
		resolve(writeData);
	});
};

export const sendErrorEmail = async(params) => {
	const url = process.env.REACT_APP_ERROR_EMAIL;
	await cloudFunctionV2(url, params);	
};

export const mixpanelSetup = async(email, name, userId) => {
	const userObject = {
		'$email': email,
		'$name': name,
	};
	mixpanel.register({
		'Email': email,
		'Name': name
	});
	mixpanel.identify(userId);
	mixpanel.people.set(userObject);

	FullStory.identify(userId, {
		displayName: name,
		email: email,
		uid: userId
	});

	posthog?.identify(userId, {
		email: email,
		name: name
	});
}

export const getWeightedScore = (scores) => {
	const locationScore = scores[0];
	const recencyScore = scores[1];
	const similarityScore = scores[2];

	const locationWeighting = scoreWeightings[0];
	const recencyWeighting = scoreWeightings[1];
	const similarityWeighting = scoreWeightings[2];

	const overallScore = ((locationScore * locationWeighting) + (recencyScore * recencyWeighting) + (similarityScore * similarityWeighting));
	return overallScore;
};

const calculatePercentageChangePrice = (property) => {
	const listingStatus = property.listingDetails.listingStatus;
	if ( listingStatus === "for_sale" || listingStatus === "pending" ) {
		if ( property.listingDetails.listSalePriceDifference === 0 ) {
			return 0;
		}

		const oldValue = property.listingDetails.saleListingPrice;
		const newValue = property.listingDetails.listingPrice;
		const percentageChange = ((newValue - oldValue) / oldValue);
		return percentageChange;
	}
	else {
		if ( property.listingDetails.saleSoldPriceDifference === 0 ) {
			return 0;
		}
		const oldValue = property.listingDetails.saleListingPrice;
		const newValue = property.listingDetails.soldListingPrice;
		const percentageChange = ((newValue - oldValue) / oldValue);
		return percentageChange;
	}
};

export const regenerateTimeline = async(properties) => {
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const listingStatus = element.listingDetails.listingStatus;
		const priceHistory = element.priceHistory;
		const priceDifference = await calculatePercentageChangePrice(element);
		const formattedPriceDifference = percentage.format(priceDifference);
		const priceDifferenceText = formattedPriceDifference === "0%" ? null : priceDifference < 0 ? `${formattedPriceDifference} below initial list price` : `+${formattedPriceDifference} above initial list price`;

		if ( listingStatus === "for_sale" || priceHistory.length === 1 ) {
			// Property is for sale

			const priorListingIndex = priceHistory.findIndex(e => e.event === "listed_for_sale");
			if ( priorListingIndex === -1 ) {
				// If the property was never listed, show empty array
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				const listedDateDifference = moment().diff(moment(priceHistory[priorListingIndex].date), "days");

				const shortenedHistory = priceHistory.slice(0, priorListingIndex + 1);
				const priceCutIndex = shortenedHistory.findIndex(e => e.event === "price_change");
				const priceCutObject = priceCutIndex === -1 ? null : {
					label: "Last price change",
					price: shortenedHistory[priceCutIndex].price,
					text: `${listedDateDifference} day${listedDateDifference === 1 ? "" : "s"} on market`,
					textColour: listedDateDifference < 15 ? "body-bold colour-green" : listedDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
					subText: null,
					subTextColour: null,
					abstract: false,
					date: moment(shortenedHistory[priceCutIndex].date).format("MMM DD, YYYY"),
					border: listedDateDifference < 15 ? "novation-timeline-element-border-green" : listedDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
					difference: null
				};

				const listedObject = {
					label: "Listed",
					price: priceHistory[priorListingIndex].price,
					text: priceCutIndex !== -1 ? null : `${listedDateDifference} day${listedDateDifference === 1 ? "" : "s"} on market`,
					textColour: priceCutIndex !== -1 ? null : listedDateDifference < 15 ? "body-bold colour-green" : listedDateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
					subText: null,
					subTextColour: null,
					abstract: false,
					date: moment(priceHistory[priorListingIndex].date).format("MMM DD, YYYY"),
					border: listedDateDifference < 15 ? "novation-timeline-element-border-green" : listedDateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
					difference: null
				};

				const presentObject = {
					label: "Present",
					price: getPriceField(element),
					text: null,
					textColour: null,
					subText: null,
					subTextColour: null,
					abstract: false,
					date: moment().format("MMM DD, YYYY"),
					border: "novation-timeline-element-border-transparent",
					difference: priceDifferenceText
				}
				element.novationTimeline = priceCutObject !== null ? [listedObject, priceCutObject, presentObject] : [listedObject, presentObject];
				element.novationDOM = listedDateDifference;
			}
		}
		else if ( listingStatus === "pending" ) {
			// Property is pending
			const pendingDate = priceHistory[0].date;
			const priorToPendingEvent = priceHistory[1].event;
			const priorToPendingDate = priceHistory[1].date;
			const dateDifference = moment(pendingDate).diff(moment(priorToPendingDate), "days");

			const priorListingIndex = priceHistory.findIndex(e => e.event === "listed_for_sale");
			const listedToPendingDateDifference = moment(pendingDate).diff(moment(priceHistory[priorListingIndex].date), "days");
			if ( priorListingIndex === -1 ) {
				// If the property was never listed, show empty array
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				// Price at pending
				const pendingObject = {
					label: "Pending",
					price: priceHistory[0].price,
					text: null,
					textColour: null,
					subText: null,
					subTextColour: null,
					abstract: false,
					date: moment(pendingDate).format("MMM DD, YYYY"),
					border: "novation-timeline-element-border-transparent",
					difference: priceDifferenceText
				};

				// Price when originally listed
				const listedObject = {
					label: "Listed",
					price: priceHistory[priorListingIndex].price,
					text: null,
					textColour: null,
					subText: null,
					subTextColour: null,
					abstract: false,
					date: moment(priceHistory[priorListingIndex].date).format("MMM DD, YYYY"),
					border: "novation-timeline-element-border-default",
					difference: null
				};

				if ( priorListingIndex === 1 || priorToPendingEvent !== "price_change" ) {
					// If there are no price changes, just save Listed + Pending
					listedObject.text = `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`;
					listedObject.textColour = dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error";
					listedObject.abstract = true;
					listedObject.border = dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red";
					listedObject.subText = `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`;
					listedObject.subTextColour = "body-regular text-italic colour-secondary";
					element.novationTimeline = [listedObject, pendingObject];
				}
				else {
					// If there is a price change, add that in too
					const priceCutObject = {
						label: "Last price change",
						price: priceHistory[1].price,
						text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`,
						textColour: dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
						subText: `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`,
						subTextColour: "body-regular text-italic colour-secondary",
						abstract: true,
						date: moment(priorToPendingDate).format("MMM DD, YYYY"),
						border: dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
						difference: null
					};
					element.novationTimeline = [listedObject, priceCutObject, pendingObject];
				}
				element.novationDOM = dateDifference;
			}
		}
		else {
			// Property is sold
			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			const shortenedHistory = priceHistory.slice(lastSoldIndex, priceHistory.length);
			const secondObject = shortenedHistory.length === 1 ? null : shortenedHistory[1];
			const priorListingIndex = shortenedHistory.findIndex(e => e.event === "listed_for_sale");
			if ( 
				priorListingIndex === -1 || 
				shortenedHistory.length === 1 || 
				secondObject.event === "sold" || 
				secondObject.event === "removed" ||
				secondObject.event === "listed_for_rent"
			) {
				// If the property was never listed prior to being sold, 
				// then it was likely sold off-market and so there's no timeline
				element.novationTimeline = [];
				element.novationDOM = null;
			}
			else {
				const recentHistory = shortenedHistory.slice(0, priorListingIndex + 1);
				if ( recentHistory.length === 1 ) {
					// If there are no prior events, just show the sold price
					element.novationTimeline = [];
					element.novationDOM = null;
				}
				else if ( recentHistory[1].event === "pending" && recentHistory.length > 2 ) {
					// If the event directly before the sale was a pending event, handle that here
					const priorToPendingEvent = recentHistory[2].event;
					const pendingDate = recentHistory[1].date;
					const priorToPendingDate = recentHistory[2].date;
					const dateDifference = moment(pendingDate).diff(moment(priorToPendingDate), "days");

					const listedToPendingDateDifference = moment(pendingDate).diff(moment(priceHistory[priorListingIndex].date), "days");

					const listedObject = {
						label: "Listed",
						price: recentHistory[priorListingIndex].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: moment(recentHistory[priorListingIndex].date).format("MMM DD, YYYY"),
						border: "novation-timeline-element-border-default",
						difference: null
					}

					// Pending event + pending price
					const pendingObject = {
						label: "Pending",
						price: recentHistory[1].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: moment(pendingDate).format("MMM DD, YYYY"),
						border: "novation-timeline-element-border-default",
						difference: null
					};

					// Sold event + sold price
					const soldObject = {
						label: "Sold",
						price: recentHistory[0].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: moment(recentHistory[0].date).format("MMM DD, YYYY"),
						border: "novation-timeline-element-border-transparent",
						difference: priceDifferenceText
					};

					if ( priorToPendingEvent === "price_change" ) {
						// If there were price changes before the pending event, handle that here

						const priceChangeObject = {
							label: "Last price change",
							price: recentHistory[2].price,
							text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`,
							textColour: dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error",
							subText: `${listedToPendingDateDifference} total day${listedToPendingDateDifference === 1 ? "" : "s"} on market`,
							subTextColour: "body-regular text-italic colour-secondary",
							abstract: true,
							date: moment(priorToPendingDate).format("MMM DD, YYYY"),
							border: dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
							difference: null
						};

						element.novationTimeline = [listedObject, priceChangeObject, pendingObject, soldObject];

					}
					else {
						// No price changes. just Listed -> Pending -> Sold

						listedObject.text = `${dateDifference} day${dateDifference === 1 ? "" : "s"} until pending`;
						listedObject.textColour = dateDifference < 15 ? "body-bold colour-green" : dateDifference < 46 ? "body-bold colour-orange" : "body-bold colour-error";
						listedObject.abstract = true;
						listedObject.border = dateDifference < 15 ? "novation-timeline-element-border-green" : dateDifference < 46 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red";
						listedObject.subText = `${dateDifference} total day${dateDifference === 1 ? "" : "s"} on market`;
						listedObject.subTextColour = "body-regular text-italic colour-secondary";

						element.novationTimeline = [listedObject, pendingObject, soldObject];
					}
					element.novationDOM = dateDifference;
				}
				else {
					// Go straight from For Sale to Sold
					const priorToSoldEvent = recentHistory[1].event;
					const priorToSoldDate = recentHistory[1].date;
					const dateDifference = moment(recentHistory[0].date).diff(moment(priorToSoldDate), "days");
					const abstractObject = {
						label: priorToSoldEvent === "price_change" ? "Last price change" : "Listed",
						price: recentHistory[1].price,
						text: `${dateDifference} day${dateDifference === 1 ? "" : "s"} until sold`,
						textColour: dateDifference < 45 ? "body-bold colour-green" : dateDifference < 91 ? "body-bold colour-orange" : "body-bold colour-error",
						subText: null,
						subTextColour: null,
						abstract: true,
						date: moment(priorToSoldDate).format("MMM DD, YYYY"),
						border: dateDifference < 45 ? "novation-timeline-element-border-green" : dateDifference < 91 ? "novation-timeline-element-border-orange" : "novation-timeline-element-border-red",
						difference: null
					};

					const soldObject = {
						label: "Sold",
						price: recentHistory[0].price,
						text: null,
						textColour: null,
						subText: null,
						subTextColour: null,
						abstract: false,
						date: moment(recentHistory[0].date).format("MMM DD, YYYY"),
						border: "novation-timeline-element-border-transparent",
						difference: priceDifferenceText
					};

					element.novationTimeline = [abstractObject, soldObject];
					element.novationDOM = dateDifference;
				}
			}
		}
	};

	return properties;
};

export const generateNovationScores = async(properties) => {
	const novationGroups = [];
	for (let index = 0; index < properties.length; index++) {
		const element = properties[index];
		const overallScore = element.compDetails.overallScore;
		if ( overallScore < 0.8 ) {
			continue;
		}

		const listingDetails = element.listingDetails;
		if ( listingDetails.listingStatus !== "sold" ) {
			continue;
		}

		const priceHistory = element.priceHistory;
		if ( priceHistory === undefined ) {
			continue;
		}

		// Original listing price
		const originalPrice = listingDetails.saleListingPrice;

		// Final sold price
		const soldPrice = listingDetails.soldListingPrice;

		// How many times was the price changed WHILST being actively for sale
		// NB - Even if there are 0 price changes, the final sold price could still be different 
		// if there was a bidding war, but the MLS simply was never updated
		const totalPriceChanges = listingDetails.totalPriceChanges;

		const lastPendingIndex = priceHistory.findIndex(e => e.event === "pending");
		const lastPendingObject = lastPendingIndex === -1 ? null : priceHistory[lastPendingIndex];
		const lastPendingDate = lastPendingObject === null ? null : lastPendingObject.date;
		const lastPendingDateDifference = lastPendingDate === null ? null : moment().diff(moment(lastPendingDate), "months");

		if ( (lastPendingIndex === -1) || (lastPendingDateDifference > 12) || ((lastPendingIndex + 1) === priceHistory.length) || (priceHistory[lastPendingIndex + 1].event === "removed") || (priceHistory[lastPendingIndex + 1].event === "listed_for_rent") ) {
			// If there are no pendings or the last pending was over 1 year ago, just focus on time to go sold.
			// Also if the last pending event has no prior events, just focus on sold

			// Excluding comps where no sold event exists, or if that's there are no prior events to it being sold
			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			if ( lastSoldIndex === -1 || (lastSoldIndex + 1 === priceHistory.length) ) {
				continue;
			}

			// If the event prior to the sale was a listing for rent, or a sale, or a removal, then this comp can't inform us
			// on how long it took to go pending
			const priorToSaleEvent = priceHistory[lastSoldIndex + 1].event;
			if ( priorToSaleEvent === "listed_for_rent" || priorToSaleEvent === "sold" || priorToSaleEvent === "removed" ) {
				continue;
			}

			const lastSoldObject = priceHistory[lastSoldIndex];
			const eventBeforeSold = priceHistory[lastSoldIndex + 1];
			const dateDifferenceFromSold = moment(lastSoldObject.date).diff(moment(eventBeforeSold.date), "days");
			const relevantPriceHistory = priceHistory.slice(lastSoldIndex, (lastSoldIndex + 2));
			const newObject = {
				daysToPending: null,
				zpid: element.uniqueIds.zillowId,
				priceHistory: relevantPriceHistory,
				condition: element.compDetails.propertyCondition,
				pendingPrice: null,
				pending: false,
				soldPrice: soldPrice,
				originalPrice: originalPrice,
				totalPriceChanges: totalPriceChanges,
				daysToSale: dateDifferenceFromSold,
				overallScore: element.compDetails.overallScore,
				link: element.propertyUrls.zillowUrl
			};
			novationGroups.push(newObject);
		}
		else {
			const eventBeforePending = priceHistory[lastPendingIndex + 1];
			const dateDifferenceFromPending = moment(lastPendingDate).diff(moment(eventBeforePending.date), "days");

			const lastSoldIndex = priceHistory.findIndex(e => e.event === "sold");
			const relevantPriceHistory = priceHistory.slice(lastSoldIndex, (lastPendingIndex + 2));
			const newObject = {
				daysToPending: dateDifferenceFromPending,
				zpid: element.uniqueIds.zillowId,
				priceHistory: relevantPriceHistory,
				condition: element.compDetails.propertyCondition,
				pendingPrice: lastPendingObject.price,
				pending: true,
				soldPrice: soldPrice,
				originalPrice: originalPrice,
				totalPriceChanges: totalPriceChanges,
				daysToSale: null,
				overallScore: element.compDetails.overallScore,
				link: element.propertyUrls.zillowUrl
			}
			novationGroups.push(newObject);

			// Subject property = 163057470

			// ZPID = 45096204...property started at $465k -> $415k
			// ZPID = 45095602...property started at $350k -> $435k

			// if ( newObject.zpid === "45096204" || newObject.zpid === "45095602" ) {
			// 	console.log(newObject)
			// }
		}

		// Map comps by time to go pending
		// 1. Price points for 1 month or less
		// 2. Price points for 2 monts or less
		// 2. Price points for 3 monts or less
		// 2. Price points for 4 monts or less
		// 2. Price points for 5 monts or less
		// 2. Price points for 6 monts or less
	};

	const novationScores = [];
	for (let index = 0; index < novationGroups.length; index++) {
		const element = novationGroups[index];
		const condition = element.condition;
		const conditionIndex = novationScores.findIndex(e => e.condition === condition);
		if ( conditionIndex === -1 ) {
			const newObject = {
				condition: condition,
				group: [element]
			};
			novationScores.push(newObject);
		}
		else {
			novationScores[conditionIndex].group.push(element);
		}
	};

	novationScores.sort((a, b) => a.condition.localeCompare(b.condition));
	return novationScores;
};

export const getFlipDetails = (property) => {
	const flipStatus = property.compDetails.flop === true ? "Flop" : property.compDetails.flip === true ? "Flip" : "Non-flip";
	const flipBGColour = flipStatus === "Flop" ? "bg-colour-flop" : flipStatus === "Flip" ? "bg-colour-flip" : "bg-colour-non-flip";
	const flipBGColourDark = flipStatus === "Flop" ? "bg-colour-flop-dark" : flipStatus === "Flip" ? "bg-colour-flip-dark" : "bg-colour-non-flip-dark";
	const flippingDescription = property.listingDetails.listingStatus === "for_sale" || property.listingDetails.listingStatus === "pending" ? "Property is being flipped, specifically remodeled for re-sale." : "Property was flipped, specifically remodeled for re-sale."
	const flipDescription = flipStatus === "Flop" ? "Property is a flip, but is struggling to sell. On market for over 90 days." : flipStatus === "Flip" ? flippingDescription : "Excellent condition comp, but not remodeled specifically for re-sale.";
	const flipDotcolour = flipStatus === "Flop" ? statusDotColours.flop : flipStatus === "Flip" ? statusDotColours.flip : statusDotColours.nonFlip;
	return { flipStatus, flipBGColour, flipDescription, flipDotcolour, flipBGColourDark };
};

export const generateARV = async(subjectProperty, comps, ceilings, redfinData) => {
	const gptURL = process.env.REACT_APP_QUERY_GPT;
	const promptPayload = arvPrompts.initial({
		comps: comps,
		subjectProperty: subjectProperty,
		ceilings: ceilings,
		redfinData: redfinData
	});

	const prompt = promptPayload.prompt;
	const systemInstructions = promptPayload.system;

	return new Promise(async resolve => {
		const gptParams = {
			service: "openai",
			model: "o3",
			prompt: prompt,
			systemInstructions: systemInstructions,
			responseFormat: {
				type: "json_object"
			}
		};
		const queryGPTData = await cloudFunctionV2(gptURL, gptParams);
		if ( queryGPTData.status === 200 ) {
			const res = {
				status: 200,
				data: queryGPTData.data
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryGPTData.error
			};
			resolve(res);
		}
	});
};

export const generateAsIs = async(subjectProperty, comps) => {
	const gptURL = process.env.REACT_APP_QUERY_GPT;
	const promptPayload = asIsPrompts.initial({
		comps: comps,
		subjectProperty: subjectProperty
	});
	const prompt = promptPayload.prompt;
	const systemInstructions = promptPayload.system;

	return new Promise(async resolve => {
		const gptParams = {
			service: "openai",
			model: "o3",
			prompt: prompt,
			systemInstructions: systemInstructions,
			responseFormat: {
				type: "json_object"
			}
		};
		const queryGPTData = await cloudFunctionV2(gptURL, gptParams);
		if ( queryGPTData.status === 200 ) {
			const res = {
				status: 200,
				data: queryGPTData.data
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryGPTData.error
			};
			resolve(res);
		}
	});
};

export const renderHTML = (inputText) => {
	// Make the change **before** you first call `marked.parse`
	marked.use({
		tokenizer: {
			del(src) {
				// match ONLY double‑tilde blocks
				const cap = /^~~(?=\S)([\s\S]*?\S)~~/.exec(src);
				if (cap) {
					return {
						type: 'del',
						raw:  cap[0],
						text: cap[1],
						tokens: this.lexer.inlineTokens(cap[1]) // keep inner markdown working
					};
				}
			}
		}
	});
    const rawHTML = marked(inputText); // Convert Markdown to HTML
    return DOMPurify.sanitize(rawHTML); // Sanitize output
};

export const cleanGPTMessages = (arvMessages) => {
	const cleanedMessages = arvMessages
		.filter(message => message.role !== "user_chat")
		.filter(message => message.role !== "event")
		.filter(message => message.role !== "action_required")
		.map(({ role, content }) => ({ role, content }));
	return cleanedMessages;
};

export const queryARVChat = async(arvMessages) => {
	const gptURL = process.env.REACT_APP_CLOZERS_GPT;
	const cleanedMessages = await cleanGPTMessages(arvMessages);

	return new Promise(async resolve => {
		const params = {
            service: "openai",
            messages: cleanedMessages,
            model: "o3",
			responseFormat: {
				type: "json_object"
			}
        };
		const queryGPTData = await cloudFunctionV2(gptURL, params);
		if ( queryGPTData.status === 200 ) {
			const res = {
				status: 200,
				data: queryGPTData.data
			}
			resolve(res);
		}
		else {
			const res = {
				status: 400,
				error: queryGPTData.error
			};
			resolve(res);
		}
	});
};

export const setPropertyARVMessages = async(colRef, docRef, subColRef, subDocRef, arvMessages, arvData, actionRequired) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;

				if ( arvData.arvRegeneration === true && data.arvData !== undefined ) {
					data.arvData.arv = arvData.arv;
					data.arvData.arvRange = arvData.arvRange;
					data.arvData.description = arvData.description;
					if ( arvData.comps !== undefined ) {
						data.arvData.comps = arvData.comps;
					}
				}

				if ( actionRequired === true && data.arvData.scenarios !== undefined && data.arvData.scenarios !== null ) {
					if ( arvData.arv === data.arvData.scenarios.optimistic.arv ) {
						data.arvData.scenarios.optimistic.selected = true;

						const newArvMessage = {
							role: "event",
							content: "Optimistic strategy selected",
							date: new Date()
						};
						arvMessages.push(newArvMessage);
					}
					else if ( arvData.arv === data.arvData.scenarios.conservative.arv ) {
						data.arvData.scenarios.conservative.selected = true;

						const newArvMessage = {
							role: "event",
							content: "Conservative strategy selected",
							date: new Date()
						};
						arvMessages.push(newArvMessage);
					}
				}

				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const selectARVScenario = async(colRef, docRef, subColRef, subDocRef, arvMessages, arvData, optimistic) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvMessages = arvMessages;
				data.arvData.arv = arvData.arv;
				data.arvData.arvRange = arvData.arvRange;
				data.arvData.description = arvData.description;

				if ( optimistic ) {
					data.arvData.scenarios.optimistic.selected = true;
				}
				else {
					data.arvData.scenarios.conservative.selected = true;
				}

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const updatePropertyARVMessages = async(colRef, docRef, subColRef, subDocRef, arvMessages) => {
	const db = getFirestore();
	const queryParams = doc(db, colRef, docRef, subColRef, subDocRef);
	let res;
	try {
		await runTransaction(db, async (transaction) => {
			const query = await transaction.get(queryParams);
			if (!query.exists()) {
				res = {
					status: 400,
					message: `Transaction failed: document doesn't exist`
				};
			}
			else {
				let data = query.data().data;
				
				data.arvMessages = arvMessages;

				transaction.update(queryParams, {
					data
				});
				res = {
					status: 200,
					message: "Transaction successfully committed"
				};
			}
		});

		return res;
	} 
	catch (e) {
		const res = {
			status: 400,
			message: `Transaction error: ${e}`
		};
		return res;
	}
};

export const queryDisqualifyingStatement = async(subjectPropertySubmission, bestCompSubmission, propertySubmission) => {
	const url = process.env.REACT_APP_CLOZERS_GPT;
	const promptPayload = disqualifierPrompts.initial({
		comps: bestCompSubmission,
		subjectProperty: subjectPropertySubmission,
		selectedComp: propertySubmission
	})
	const prompt = promptPayload.prompt;
	const systemInstructions = promptPayload.system;
	const parts = [
		{
			text: prompt
		}
	];

	const newElement = {
		role: "user",
		parts: parts
	};
	const contents = [newElement];

	const params = {
		service: "gemini",
		messages: [
			{
				role: "user",
				content: prompt
			}
		],
		model: "gemini-2.0-flash",
		modelOptions: {
			model: "gemini-2.0-flash",
			systemInstruction: systemInstructions
		},
		contents: {
			contents: contents
		}
	};

	return new Promise(async resolve => {
		const queryData = await cloudFunctionV2(url, params);
		resolve(queryData);
	});
};

export const getNames = (words) => {
	const n = words.split(" ");
	const object = {
		firstName: n[0],
		lastName: n[n.length - 1]
	}
	return object;
}

export const completeSignUp = async(
	signUpUser, 
	googleAuth,
	email,
	firstName,
	lastName
) => {
	await setMixpanelAlias(signUpUser.user.uid);
	const mode = window.location.hostname.includes("clozers") ? "live" : "test";
	const stripeCustomerURL = process.env.REACT_APP_CREATE_STRIPE_CUSTOMER;
	const customer = {
		email: googleAuth === false ? email : signUpUser.user.email,
		name: googleAuth === false ? `${firstName} ${lastName}` : signUpUser.user.displayName,
		phone: "",
		mode: mode
	};
	const getCustomer = await cloudFunctionV2(stripeCustomerURL, customer);
	const colRef = "users";
	const docRef = signUpUser.user.uid;
	const displayName = googleAuth === false ? `${firstName} ${lastName}` : signUpUser.user.displayName;
	const displayEmail = googleAuth === false ? email : signUpUser.user.email;
	const names = googleAuth === false ? null : getNames(displayName);
	const data = {
		companyId: "",
		email: googleAuth === false ? email : displayEmail,
		firstName: googleAuth === false ? firstName : names.firstName,
		lastName: googleAuth === false ? lastName : names.lastName,
		phoneNumber: "",
		phoneNumberPrefix: "+1",
		phoneNumberVerified: false
	};

	if ( getCustomer.status === 200 ) {
		data.customerId = getCustomer.customerId;
	}

	const createDoc = await setData(colRef, docRef, data);
	if ( createDoc.status === 200 ) {
		recordEvent("account_created", {
			userId: docRef,
			email: googleAuth === false ? email : signUpUser.user.email,
		});

		if ( googleAuth === true ) {
			recordEvent("email_verified", {});
		}

		const mailchimpURL = process.env.REACT_APP_MAILCHIMP_API;
		const mailchimpObject = {
			list_id: "2eac986d19",
			get: false,
			values: {
				email_address: data.email,
				status: "subscribed",
				merge_fields: {
					FNAME: data.firstName,
					LNAME: data.lastName,
					PHONE: ""
				}

			}
		}
		cloudFunctionV2(mailchimpURL, mailchimpObject);
	}
};

export const queryATTOM = async(streetAddress, city, state, zipcode) => {
	return new Promise(async resolve => {
		const url = process.env.REACT_APP_ATTOM_QUERY;
		const params = {
			address1: streetAddress,
			address2: `${city}, ${state} ${zipcode}`
		};
		const queryData = await cloudFunctionV2(url, params);
		resolve(queryData);
	});
};

export const listenForARVMessages = async(
	companyId,
	propertyId,
	setArvMessages,
	setArvEstimate,
	setArvDescription,
	setArvRange,
	url,
	params,
	oldArvMessages,
    oldArvEstimate,
	allComps,
	viewComp,
	setTypeOutText
) => {
	// ---- one‑off listener while the ARV Cloud Function runs ----
	const db = getFirestore();
	const ref = doc(
		db,
		"companies",
		companyId,
		"properties",
		propertyId
	);
	// listener ends after two chat updates OR one arv update (whichever happens first)
	let chatUpdateCount = 0;
	let latestMsgLength = oldArvMessages.length;
	let arvUpdated = false;
	let completed = false;

	const unsubscribeArvListener = onSnapshot(ref, (snap) => {
        const docData = snap.data().data;

		/* ---- diff‑aware updates ---- */
		// Stream chat updates only if they changed
		if (
			docData.arvMessages &&
			Array.isArray(docData.arvMessages) &&
			typeof setArvMessages === "function"
		) {
			const sameMessages =
				JSON.stringify(docData.arvMessages) ===
				JSON.stringify(oldArvMessages);
			if ( (docData.arvMessages.length > latestMsgLength) && !sameMessages ) {
				setArvMessages(docData.arvMessages);
				setTypeOutText(true);
				chatUpdateCount++;
				latestMsgLength = docData.arvMessages.length;
			}
		}

		// Stream final ARV figures only if the ARV changed
		if (
			docData.arvData &&
			docData.arvData.arv !== undefined &&
			docData.arvData.arv !== null &&
			typeof setArvEstimate === "function"
		) {
			if (docData.arvData.arv !== oldArvEstimate) {
				setArvEstimate(docData.arvData.arv);
				setArvDescription(docData.arvData.description);
				setArvRange(docData.arvData.arvRange);
				arvUpdated = true;
			}
		}

		if (arvUpdated || chatUpdateCount >= 2) {
			unsubscribeArvListener();
		}
		else if (completed) {
			unsubscribeArvListener();
		}
	});
	const queryData = await cloudFunctionV2(url, params);
	if ( queryData.status !== 200 && chatUpdateCount > 0 ) {
		unsubscribeArvListener();
	}
	else if ( queryData.status !== 200 ) {
		completed = true;
	}
	
	if ( queryData.status === 200 && queryData.data.comps.length > 0 ) {
		const compIndex = allComps.findIndex(e => e.uniqueIds.zillowId === queryData.data.comps[0]);
		if ( compIndex !== -1 ) {
			viewComp(allComps[compIndex]);
		}
	}
}

export const getExclusionDescription = (item, subjectProperty, comp) => {
	const subjectPropertyConstructionType = subjectProperty.propertyAttributes.constructionTypeBySource === undefined || subjectProperty.propertyAttributes.constructionTypeBySource === null || subjectProperty.propertyAttributes.constructionTypeBySource.reapi === null ? subjectProperty.propertyAttributes.constructionType : subjectProperty.propertyAttributes.constructionTypeBySource.reapi;
	const constructionType = comp.propertyAttributes.constructionTypeBySource === undefined || comp.propertyAttributes.constructionTypeBySource === null || comp.propertyAttributes.constructionTypeBySource.reapi === null ? comp.propertyAttributes.constructionType : comp.propertyAttributes.constructionTypeBySource.reapi;

	const subjectPool = subjectProperty.propertyAttributes.pool;
	const compPool = comp.propertyAttributes.pool;

	const subjectBeds = subjectProperty.propertyAttributes.bedrooms;
	const compBeds = comp.propertyAttributes.bedrooms;

	const subjectBaths = subjectProperty.propertyAttributes.bathrooms;
	const compBaths = comp.propertyAttributes.bathrooms;

	const subjectStories = subjectProperty.propertyAttributes.stories;
	const compStories = comp.propertyAttributes.stories;

	const compADU = comp.propertyAttributes.adu;

	const descriptions = {
		exclude_rules: `**Disqualifier -** This comp was deemed to be an outlier because of its characteristics`,
		low_similarity: `**Disqualifier -** This comp was too dissimilar to the subject property`,
		outlier: `**Disqualifier -** This comp was deemed to be an outlier because of its characteristics`,
		user_excluded: `**Disqualifier -** This comp was excluded by you when you chose your price ceiling`,
		unconventional_market: `**Disqualifier -** This comp had unconventional market conditions`,
		construction_material: `**Disqualifier -** This comp has a ${constructionType} construction material whereas the subject property has a ${subjectPropertyConstructionType} construction material`,
		pool_filter: `**Disqualifier -** This comp ${compPool === true ? "has" : "does not have"} a pool whereas the subject property ${subjectPool === true ? "has" : "does not have"} a pool`,
		bed_bath_filter: `**Disqualifier -** This comp is a ${compBeds} bed ${compBaths} bath whereas the subject property is a ${subjectBeds} bed ${subjectBaths} bath property`,
		stories_filter: `**Disqualifier -** This comp is a ${compStories} story property whereas the subject property is a ${subjectStories} story property`,
		adu_filter: `**Disqualifier -** ${compADU === false ? "The subject property has an ADU whereas the comp does not" : "The subject property does not have an ADU whereas the comp does"}`,
		arv_comp_selection: `**Disqualifier -** This comp was excluded at the final ARV comp selection stage`
	}

	const reason = item.reason;
	const description = descriptions[reason];

	if ( item.description ) {
		return item.description;
	}
	return description;
};

export const formatPhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters
    let cleaned = ('' + phoneNumber).replace(/\D/g, '');

    // Remove leading '1' if present and length is 11
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
        cleaned = cleaned.substring(1);
    }

    // Truncate to maximum 10 digits
    cleaned = cleaned.slice(0, 10);

    const len = cleaned.length;

    if (len === 0) return '';
    if (len < 4) return `(${cleaned}`;
    if (len < 7) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

export const cleanPhoneNumber = (phoneNumber) => {
    // Remove all non-numeric characters
    const cleaned = ('' + phoneNumber).replace(/\D/g, '');

    return cleaned;
};

export const isValidURL = (value) => {
	try {
		const url = new URL(value);
		return url.protocol === "http:" || url.protocol === "https:";
	} 
	catch (_) {
		return false;
	}
}